import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, useLocation } from 'react-router-dom';
import { LoginFormInputs } from '../../types/admin';
import { signIn, subscribeToAuthChanges } from '../../utils/auth';
import Spinner from '../../components/common/Spinner';
import { toast, Toaster } from 'react-hot-toast';
import '../../styles/admin/Login.css';

export const AdminLogin: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormInputs>();

  useEffect(() => {
    const unsubscribe = subscribeToAuthChanges((user) => {
      if (user) {
        // User is logged in, redirect to dashboard
        navigate('/admin/dashboard', { replace: true });
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  const onSubmit = async (data: LoginFormInputs) => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await signIn(data.email, data.password);

      if (result.error) {
        // Handle specific Firebase error codes
        if (result.error.includes('auth/too-many-requests')) {
          toast.error(
            "Too many login attempts. Please try again in a few minutes or reset your password if you've forgotten it."
          );
        } else if (
          result.error.includes('auth/user-not-found') ||
          result.error.includes('auth/wrong-password')
        ) {
          toast.error('Invalid email or password. Please try again.');
        } else if (result.error.includes('auth/invalid-email')) {
          toast.error('Please enter a valid email address.');
        } else {
          toast.error('Invalid email or password. Please try again.');
        }
        return;
      }

      toast.success('Login successful! Redirecting...');

      // Navigate to the dashboard or the page user tried to access
      const from =
        (location.state as any)?.from?.pathname || '/admin/dashboard';
      navigate(from, { replace: true });
    } catch (err: any) {
      // Handle unexpected errors
      if (err.message?.includes('auth/too-many-requests')) {
        toast.error(
          "Too many login attempts. Please try again in a few minutes or reset your password if you've forgotten it."
        );
      } else {
        toast.error('An unexpected error occurred. Please try again later.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <Toaster position="top-center" />
      <div className="admin-login-card">
        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Please sign in to your admin account</p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="admin-login-form">
          <div className="form-group">
            <input
              id="email"
              type="email"
              placeholder=" "
              {...register('email', {
                required: 'Email is required',
                pattern: {
                  value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                  message: 'Invalid email address',
                },
              })}
              className={errors.email ? 'error' : ''}
            />
            <label htmlFor="email">Email Address</label>
            {errors.email && (
              <span className="error-message">{errors.email.message}</span>
            )}
          </div>

          <div className="form-group">
            <input
              id="password"
              type="password"
              placeholder=" "
              {...register('password', {
                required: 'Password is required',
                minLength: {
                  value: 6,
                  message: 'Password must be at least 6 characters',
                },
              })}
              className={errors.password ? 'error' : ''}
            />
            <label htmlFor="password">Password</label>
            {errors.password && (
              <span className="error-message">{errors.password.message}</span>
            )}
          </div>

          <button
            type="submit"
            className={`admin-login-button ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="button-content">
                <Spinner size="small" color="white" />
                <span>Signing in...</span>
              </div>
            ) : (
              'Sign In'
            )}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
